import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ArrowUpRight, Loader2, SendHorizontal, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { withServiceInstanceId } from '@/lib/service-instance'
import { cn } from '@/lib/utils'
import aiAssistApi, { type AiAssistParseResult } from '@/services/ai-assist'
import { workersApi } from '@/services/workers'

const SUGGESTIONS = [
  'Nearest plumber for job JOB-1042',
  'Any cleaning worker available tomorrow 9pm?',
  'How many jobs tomorrow afternoon?',
  'How much should Ravi be paid this month so far?',
  'Create a high-priority install job at 10 High St for Acme Friday 9am',
]

type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  result?: AiAssistParseResult
}

type EllanAiAssistanceDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceInstanceId?: string | null
}

function nextId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function resolveIntentHint(statement: string): 'auto' | 'job' | 'booking' | 'query' {
  const text = String(statement || '').trim().toLowerCase()
  if (!text) {
    return 'auto'
  }

  const hasCreateSignal = /\b(create|add|new|schedule|book)\b/.test(text)
  const hasJobSignal = /\b(job|task|work|cleaning|maintenance|repair|install)\b/.test(text)
  if (hasCreateSignal && hasJobSignal) {
    return 'job'
  }

  if (/\b(booking|reservation|reserve)\b/.test(text)) {
    return 'booking'
  }

  if (/\b(nearest|available|availability|count|how many|report|paid|pay|skill|skills|earnings|working|current)\b/.test(text)) {
    return 'query'
  }

  return 'auto'
}

function formatAssistantContent(result: AiAssistParseResult): string {
  if (result.intent === 'query') {
    const answer = String(result.draft?.answer_text || '').trim()
    if (answer) {
      return answer
    }
  }

  if (result.notes) {
    return result.notes
  }

  if (result.intent === 'job') {
    return result.ready
      ? 'Draft is ready. Open create form to review and save.'
      : 'Draft needs a few missing details before it can be created.'
  }

  if (result.intent === 'booking') {
    return 'That looks like a booking request. Please continue in POS / Bookings.'
  }

  if (result.intent === 'job_update' || result.intent === 'job_assignment_update') {
    return result.ready
      ? 'Update draft is ready. Please continue from job actions.'
      : 'I understood this as an update request, but need more details.'
  }

  if (result.intent === 'worker_availability_update') {
    return result.ready
      ? 'Worker availability draft is ready.'
      : 'I drafted worker availability changes, but need a bit more detail.'
  }

  return 'Please add more details like a job ref, worker name, date/time, or location.'
}

function WorkerRows({ workers }: { workers: unknown }) {
  if (!Array.isArray(workers) || workers.length === 0) {
    return null
  }

  return (
    <ul className="mt-2 space-y-1.5">
      {workers.slice(0, 8).map((row, index) => {
        const worker = row && typeof row === 'object' ? row as Record<string, unknown> : {}
        const name = String(worker.name || worker.display_name || `Worker ${worker.id ?? index + 1}`)
        const role = worker.role ? String(worker.role) : ''
        const distance = worker.distance_km != null ? `${worker.distance_km} km` : ''
        const rate = worker.rate != null ? `rate ${worker.rate}` : ''
        const phone = worker.phone ? String(worker.phone) : ''
        const meta = [role, distance, rate, phone].filter(Boolean).join(' · ')

        return (
          <li key={String(worker.id ?? index)} className="rounded-lg border border-border/60 bg-background/80 px-2.5 py-1.5 text-xs">
            <span className="font-medium text-foreground">{name}</span>
            {meta ? <span className="mt-0.5 block text-muted-foreground">{meta}</span> : null}
          </li>
        )
      })}
    </ul>
  )
}

function SummaryRows({ summary }: { summary: unknown }) {
  if (!summary || typeof summary !== 'object') {
    return null
  }

  const entries = Object.entries(summary as Record<string, unknown>).filter(([, value]) => (
    value !== null && value !== undefined && value !== ''
  ))

  if (entries.length === 0) {
    return null
  }

  return (
    <dl className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
      {entries.map(([key, value]) => (
        <div key={key} className="rounded-lg border border-border/60 bg-background/80 px-2.5 py-1.5">
          <dt className="text-muted-foreground">{key.replace(/_/g, ' ')}</dt>
          <dd className="font-medium text-foreground">{String(value)}</dd>
        </div>
      ))}
    </dl>
  )
}

function AvailabilityRows({ availability }: { availability: unknown }) {
  if (!Array.isArray(availability) || availability.length === 0) {
    return null
  }

  return (
    <ul className="mt-2 space-y-1.5">
      {availability.slice(0, 10).map((row, index) => {
        const item = row && typeof row === 'object' ? row as Record<string, unknown> : {}
        const date = String(item.date || '').trim()
        const status = item.is_available ? 'Available' : 'Unavailable'
        const windowText = [item.from_time, item.to_time].filter(Boolean).join(' - ')
        const notes = item.notes ? String(item.notes) : ''

        return (
          <li key={String(item.id ?? index)} className="rounded-lg border border-border/60 bg-background/80 px-2.5 py-1.5 text-xs">
            <span className="font-medium text-foreground">{date || `Entry ${index + 1}`}</span>
            <span className="ml-2 text-muted-foreground">{status}</span>
            {windowText ? <span className="mt-0.5 block text-muted-foreground">{windowText}</span> : null}
            {notes ? <span className="mt-0.5 block text-muted-foreground">{notes}</span> : null}
          </li>
        )
      })}
    </ul>
  )
}

function AssistantExtras({ result }: { result: AiAssistParseResult }) {
  const data = result.draft?.data && typeof result.draft.data === 'object'
    ? result.draft.data
    : null
  const fieldErrors = Object.entries(result.field_errors || {})
  const hasCreateDraft = Boolean(result.ready || String(result.draft?.title || '').trim())

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {hasCreateDraft ? (
          <Badge variant={result.ready ? 'default' : 'secondary'} className="text-[10px]">
            {result.ready ? 'Ready' : 'Needs details'}
          </Badge>
        ) : null}
        {typeof data?.count === 'number' ? (
          <Badge variant="secondary" className="text-[10px]">
            count {data.count}
          </Badge>
        ) : null}
      </div>

      {result.missing_fields.length > 0 ? (
        <p className="text-[11px] text-muted-foreground">
          Missing: {result.missing_fields.join(', ')}
        </p>
      ) : null}

      {fieldErrors.length > 0 ? (
        <ul className="list-inside list-disc text-[11px] text-destructive">
          {fieldErrors.map(([field, messages]) => (
            <li key={field}>{field}: {messages.join(', ')}</li>
          ))}
        </ul>
      ) : null}

      {data ? (
        <>
          <WorkerRows workers={data.workers} />
          <AvailabilityRows availability={data.availability} />
          <SummaryRows summary={data.summary} />
          {data.period && typeof data.period === 'object' ? (
            <p className="text-[11px] text-muted-foreground">
              Period: {String((data.period as Record<string, unknown>).month)}/
              {String((data.period as Record<string, unknown>).year)}
            </p>
          ) : null}
          {typeof data.earned_so_far === 'number' || typeof data.balance_to_pay === 'number' ? (
            <SummaryRows
              summary={{
                earned_so_far: data.earned_so_far,
                paid_so_far: data.paid_so_far,
                balance_to_pay: data.balance_to_pay,
                total_hours: data.total_hours,
              }}
            />
          ) : null}
        </>
      ) : null}

      {hasCreateDraft && result.draft?.title ? (
        <p className="text-[11px] text-muted-foreground">
          Draft title: <span className="font-medium text-foreground">{String(result.draft.title)}</span>
        </p>
      ) : null}
    </div>
  )
}

export function EllanAiAssistanceDialog({
  open,
  onOpenChange,
  serviceInstanceId,
}: EllanAiAssistanceDialogProps) {
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [draft, setDraft] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])

  const parseMutation = useMutation({
    mutationFn: (statement: string) =>
      aiAssistApi.parseCreate({
        statement,
        intentHint: resolveIntentHint(statement),
        serviceInstanceId,
      }),
    onSuccess: (result) => {
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content: formatAssistantContent(result),
          result,
        },
      ])
    },
    onError: (error) => {
      const message = getErrorMessage(error) || 'Could not reach AI assist.'
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content: message,
        },
      ])
      toast.error(message)
    },
  })

  const applyAvailabilityMutation = useMutation({
    mutationFn: async (result: AiAssistParseResult) => {
      const workerId = String(result.draft?.worker_id || '').trim()
      if (!workerId) {
        throw new Error('Worker ID was not resolved for this draft.')
      }

      const entries = Array.isArray(result.draft?.entries) ? result.draft.entries : []
      await workersApi.updateWorkerAvailability(workerId, {
        apply_weeks: Number(result.draft?.apply_weeks || 1),
        entries: entries.map((entry) => ({
          date: String((entry as Record<string, unknown>).date || '').trim(),
          is_available: Boolean((entry as Record<string, unknown>).is_available),
          from_time: String((entry as Record<string, unknown>).from_time || '').trim(),
          to_time: String((entry as Record<string, unknown>).to_time || '').trim(),
          notes: String((entry as Record<string, unknown>).notes || '').trim(),
        })),
      })
    },
    onSuccess: () => {
      toast.success('Worker availability updated.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || 'Could not update worker availability.')
    },
  })

  useEffect(() => {
    const container = scrollRef.current
    if (!container) {
      return
    }
    container.scrollTop = container.scrollHeight
  }, [messages, parseMutation.isPending, open])

  const canSend = useMemo(
    () => Boolean(serviceInstanceId) && Boolean(draft.trim()) && !parseMutation.isPending,
    [draft, parseMutation.isPending, serviceInstanceId],
  )

  const send = () => {
    const statement = draft.trim()
    if (!statement || parseMutation.isPending) {
      return
    }
    if (!serviceInstanceId) {
      toast.error('Select a workspace before using AI assistance.')
      return
    }

    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: 'user',
        content: statement,
      },
    ])
    setDraft('')
    parseMutation.mutate(statement)
  }

  const openCreateForm = (result: AiAssistParseResult) => {
    navigate(withServiceInstanceId('/job/create', serviceInstanceId), {
      state: { aiAssistDraft: result },
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!top-auto !left-auto !right-3 !bottom-3 !translate-x-0 !translate-y-0 flex h-[min(78vh,720px)] w-[min(28rem,calc(100vw-1.5rem))] max-w-none flex-col gap-0 overflow-hidden rounded-3xl border border-border/70 p-0 shadow-2xl sm:!right-6 sm:!bottom-6">
        <DialogHeader className="border-b border-border/70 px-5 py-4 text-left">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-sky-600" />
            Ellan AI Assistance
          </DialogTitle>
          <DialogDescription>
            Ask operational questions or describe a job in plain language.
          </DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {messages.length === 0 ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-5 text-center">
                <p className="text-sm font-medium text-foreground">Start a conversation</p>
                <p className="mt-1 text-xs text-muted-foreground">Try one of these prompts</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="rounded-full border border-border/70 bg-background px-3 py-1.5 text-left text-[11px] text-muted-foreground transition hover:border-sky-300 hover:text-foreground"
                    onClick={() => setDraft(suggestion)}
                    disabled={parseMutation.isPending}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {messages.map((message) => {
            const own = message.role === 'user'
            const canOpenCreate = Boolean(
              !own
              && message.result
              && message.result.intent === 'job'
              && (message.result.ready || String(message.result.draft?.title || '').trim()),
            )

            return (
              <div key={message.id} className={cn('flex', own ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm',
                    own
                      ? 'rounded-br-md bg-primary text-primary-foreground'
                      : 'rounded-bl-md border border-border/70 bg-muted/40 text-foreground',
                  )}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  {!own && message.result ? <AssistantExtras result={message.result} /> : null}

                  {canOpenCreate && message.result ? (
                    <div className="mt-3">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => openCreateForm(message.result!)}
                      >
                        Open create form
                        <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : null}
                  {message.result?.intent === 'worker_availability_update' && message.result.ready ? (
                    <div className="mt-3">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={applyAvailabilityMutation.isPending}
                        onClick={() => applyAvailabilityMutation.mutate(message.result!)}
                      >
                        {applyAvailabilityMutation.isPending ? 'Applying...' : 'Apply availability changes'}
                        <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            )
          })}

          {parseMutation.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Thinking...
            </div>
          ) : null}
        </div>

        <div className="border-t border-border/70 p-4">
          {!serviceInstanceId ? (
            <p className="mb-2 text-xs text-muted-foreground">Select a workspace before using AI assistance.</p>
          ) : null}
          <div className="flex items-end gap-2">
            <Textarea
              rows={2}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Type your request..."
              disabled={!serviceInstanceId || parseMutation.isPending}
              className="min-h-[64px] resize-none"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  send()
                }
              }}
            />
            <Button
              type="button"
              size="icon"
              className="h-10 w-10 shrink-0"
              disabled={!canSend}
              onClick={send}
            >
              {parseMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SendHorizontal className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EllanAiAssistanceDialog
